import React, { useEffect, useState } from 'react';

export interface FilterSearchFieldProps {
  id: string;
  /** The applied search; the field shows it and resets to it when it changes from outside. */
  value: string;
  /** Runs the search (Enter, or the clear button). */
  onSearch: (value: string) => void;
  /** Names what is searched (SRCH-02), e.g. "Unit #, Description, Make, Model or VIN". */
  placeholder: string;
  /** Accessible name; the field has no visible label in the header bar. */
  label?: string;
}

/**
 * The list search field (SRCH-01, SRCH-02): magnifier inside on the left, a clear "×" inside on
 * the right once there is text, Enter runs the search. Space for the "×" is always reserved, so
 * typing or clearing never shifts the field (SRCH-03, LAY-07).
 */
export const FilterSearchField: React.FC<FilterSearchFieldProps> = ({ id, value, onSearch, placeholder, label = 'Search' }) => {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);

  return (
    <div className="tw-relative tw-w-full">
      <i
        className="bi bi-search tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-sm tw-text-content-muted"
        aria-hidden="true"
      />
      <input
        id={id}
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSearch(text.trim());
          }
        }}
        className="tw-h-9 tw-w-full tw-appearance-none tw-rounded tw-border tw-border-line-input tw-bg-surface tw-pl-9 tw-pr-9 tw-text-sm tw-text-content tw-shadow-sm placeholder:tw-text-content-muted focus:tw-border-primary focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary/20 [&::-webkit-search-cancel-button]:tw-hidden"
      />
      {text && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            setText('');
            onSearch('');
          }}
          className="tw-absolute tw-right-1 tw-top-1/2 tw-flex tw-h-7 tw-w-7 tw--translate-y-1/2 tw-items-center tw-justify-center tw-rounded tw-border-0 tw-bg-transparent tw-text-content-muted hover:tw-text-content"
        >
          <i className="bi bi-x-lg tw-text-xs" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
